import { Box, Tooltip, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import InlineEdit from '~/components/InlineEdit';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { DicomVRMaxLength } from '~/domain/dicom/dicomConstants';
import { RegistrationView } from '~/domain/projections/views';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';

// Content Label (0070,0080) is VR: CS (Code String)
// Allowed: uppercase A-Z, digits 0-9, SPACE, underscore
const DICOM_CS_MAX_LENGTH = DicomVRMaxLength.SHORT;
// eslint-disable-next-line no-control-regex
const DICOM_CS_INVALID_CHARS = /[^A-Z0-9 _]/;

function validateRegistrationName(value: string): string | null {
    if (!value.trim()) {
        return 'Name is required';
    }
    if (value.length > DICOM_CS_MAX_LENGTH) {
        return `Max ${DICOM_CS_MAX_LENGTH} characters`;
    }
    if (DICOM_CS_INVALID_CHARS.test(value)) {
        return 'Only uppercase letters, digits, spaces, and underscores';
    }
    return null;
}

interface RegistrationHeaderProps {
    activeRegistration: RegistrationView | null;
    onRename: (newLabel: string) => void;
    isRenaming: boolean;
    onStartRename: () => void;
    onCancelRename: () => void;
}

export default function RegistrationHeader({
    activeRegistration,
    onRename,
    isRenaming,
    onStartRename,
    onCancelRename,
}: RegistrationHeaderProps) {
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const registrationLabel =
        activeRegistration?.contentLabel ||
        activeRegistration?.sopInstanceUid?.slice(0, 8);

    if (isRenaming) {
        return (
            <InlineEdit
                value={activeRegistration?.contentLabel || ''}
                onSubmit={onRename}
                onCancel={onCancelRename}
                validate={validateRegistrationName}
                fontSize={14}
                fontWeight={500}
            />
        );
    }

    const canRename =
        activeRegistration &&
        !activeRegistration.isApproved &&
        showSensitiveInfo;

    return (
        <Box
            onDoubleClick={canRename ? onStartRename : undefined}
            sx={{ cursor: canRename ? 'pointer' : 'default' }}
        >
            {/* The full name, which the line may cut short. It is PHI, so
                the tooltip shows only while patient data is shown. */}
            <Tooltip
                title={showSensitiveInfo ? registrationLabel : undefined}
                placement="top"
            >
                <Typography fontSize={14} fontWeight={500} noWrap>
                    {registrationLabel ? (
                        <SensitiveInfoTypography>
                            {registrationLabel}
                        </SensitiveInfoTypography>
                    ) : (
                        <FormattedMessage id="registration" />
                    )}
                </Typography>
            </Tooltip>
        </Box>
    );
}
